import { z } from 'zod';

export const adminLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type AdminLoginInput = z.infer<typeof adminLoginSchema>;

export const adminRefreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type AdminRefreshInput = z.infer<typeof adminRefreshSchema>;

/**
 * Platform-admin plan assignment. FREE is the usable floor rather than a sold
 * plan, so it takes no expiry — the monthly tick only degrades plans with a
 * price. Only ACTIVE and TRIALING are offered: pricing v3 deleted every code
 * path that produces PAST_DUE / EXPIRED / CANCELLED, so setting one by hand
 * would strand an org in a state nothing handles.
 */
export const assignPlanSchema = z
  .object({
    planCode: z.enum(['FREE', 'PLUS', 'PRO', 'CENTER']),
    status: z.enum(['ACTIVE', 'TRIALING']).default('ACTIVE'),
    until: z.string().datetime().optional(),
    billingCycle: z.enum(['MONTHLY', 'ANNUAL']).optional(),
    note: z.string().max(500).optional(),
  })
  .refine((v) => v.planCode === 'FREE' || v.until !== undefined, {
    message: 'A paid plan needs an expiry date',
    path: ['until'],
  })
  .refine((v) => v.planCode !== 'FREE' || v.status === 'ACTIVE', {
    message: 'The free floor cannot be a trial',
    path: ['status'],
  })
  .refine((v) => v.planCode === 'FREE' || v.billingCycle !== undefined, {
    message: 'A paid plan needs a billing cycle',
    path: ['billingCycle'],
  });

/**
 * THI-257: `POST /admin/demo/reset` body. The two slugs are duplicated
 * here rather than imported (this package cannot depend on `apps/api`'s
 * `DEMO_ORG_SLUGS` in `services/demoSeed.ts` — same precedent as
 * `assignPlanSchema`'s hardcoded plan codes above). `DEMO_RESET_SLUGS` is
 * exported (not just the schema) so `apps/api/src/routes/adminDemo.ts` can
 * run a compile-time mutual-assignability check against `DEMO_ORG_SLUGS` —
 * the two lists drifting silently is exactly the failure mode a runtime-only
 * duplication invites. THI-267 Task 9: `demo-moi` is retired and no longer
 * seeded — dropped here too so an admin request naming it 400s like any
 * other unknown slug.
 */
export const DEMO_RESET_SLUGS = ['demo-vanhanh', 'demo-anhduong'] as const;
export type DemoResetSlug = (typeof DEMO_RESET_SLUGS)[number];
export const demoResetSchema = z.object({
  slug: z.enum(DEMO_RESET_SLUGS),
});
export type DemoResetInput = z.infer<typeof demoResetSchema>;
export type AssignPlanInput = z.infer<typeof assignPlanSchema>;

export const changeUserEmailSchema = z.object({
  email: z.string().email(),
});
export type ChangeUserEmailInput = z.infer<typeof changeUserEmailSchema>;
